import Breadcrumbs from '@mui/material/Breadcrumbs';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';
import ChevronRightOutlinedIcon from '@mui-symbols-material/w400/ChevronRightOutlined';
import HomeOutlinedIcon from '@mui-symbols-material/w400/HomeOutlined';
import styles from './CdfBreadcrumbs.module.scss';

/**
 * Represents a single breadcrumb item
 */
interface BreadcrumbItem {
  /** The display text for the breadcrumb item */
  readonly label: string;
  /** Optional link path. If provided, renders as a Link. If omitted, renders as Typography (current page) */
  readonly href?: string;
}

/**
 * Props for the CdfBreadcrumbs component
 */
interface CdfBreadcrumbsProps {
  /** Array of breadcrumb items to display after the home icon */
  readonly items: BreadcrumbItem[];
}

/**
 * Common breadcrumb component that provides consistent navigation structure.
 * Always includes a home icon link to "/" followed by the provided items.
 * Items with href are rendered as links, items without href are rendered as Typography (current page).
 *
 * @param props - Component props
 * @returns Breadcrumb navigation component
 *
 * @example
 * ```tsx
 * <CdfBreadcrumbs
 *   items={[
 *     { label: 'Business Partners', href: '/business-partners' },
 *     { label: 'Partner Details' } // current page, no href
 *   ]}
 * />
 * ```
 */
export function CdfBreadcrumbs({ items }: Readonly<CdfBreadcrumbsProps>) {
  return (
    <Breadcrumbs separator={<ChevronRightOutlinedIcon />} className={styles.breadcrumbContainer}>
      <Link to="/" className={styles.breadcrumbLink} data-testid="breadcrumb-home-link">
        <HomeOutlinedIcon />
      </Link>
      {items.map(item => {
        if (item.href) {
          return (
            <Link key={item.label} to={item.href} className={styles.breadcrumbLink}>
              {item.label}
            </Link>
          );
        }
        return (
          <Typography key={item.label} className={styles.breadcrumbText}>
            {item.label}
          </Typography>
        );
      })}
    </Breadcrumbs>
  );
}
